import React from 'react';
import { Link } from '@framework/router/Next/Link';
import { useRouter } from '@framework/next/navigation';
import cn from 'classnames';
import Tooltip, { TooltipPlacement } from '@dealroadshow/uikit-tooltip';
import styles from './actions.module.scss';
import { useDataroomTwoFactorAuthenticationContext } from '@dataroom/authentication/DataroomTwoFactorAuthenticationContext';
import User from '@users/user/User';
import { useSessionContext } from '@users/session/SessionContext';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import { IDataroomListItem } from '../../../../domain/vo/DataroomListItem';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import IconCollapseSidebar from '@dealroadshow/uikit-icon/IconCollapseSidebar';
import { IconComponentType } from '@dealroadshow/uikit-icon';

export enum VariantType {
  Outline,
  Button,
}

interface IProps {
  isMobile?: boolean,
  icon: IconComponentType,
  mobileTitle?: string,
  tooltipContent: string,
  twoFactorSpanClassname: string,
  path?: string,
  disabled?: boolean,
  onClick?: (e?: React.MouseEvent) => void,
  isActionItemVisible?: boolean,
  variant?: typeof VariantType[keyof typeof VariantType],
  tooltipPlacement?: TooltipPlacement,
  buttonTitle?: string,
  className?: string,
  dataroom: IDataroomListItem,
  route?: string,
  tenant: DataroomTenant,
  dataTest: string,
  disabledButtonTooltip?: string,
}

const ActionItem = (
  {
    className = '',
    tooltipPlacement = 'bottom',
    isActionItemVisible = false,
    onClick = () => {
    },
    disabled = false,
    path = '',
    isMobile = false,
    icon: Icon,
    mobileTitle = '',
    tooltipContent,
    twoFactorSpanClassname,
    route = '',
    tenant,
    dataroom,
    dataTest,
    variant = VariantType.Outline,
    buttonTitle,
    disabledButtonTooltip,
  }: IProps,
) => {
  const { push } = useRouter();
  const {
    setModalData,
    isSessionTwoFactorVerified,
    userEmail,
  } = useDataroomTwoFactorAuthenticationContext();
  // @ts-ignore
  const { getSession } = useSessionContext();
  const isNeedToShowTwoFactorModal =
    dataroom.twoFactor
    && !User.isSupportUserByTenant(tenant, userEmail!)
    && !isSessionTwoFactorVerified
    && !disabled;

  const eventClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (path.length === 0) {
      event.preventDefault();
    }
    onClick(event);
  };

  const routeContent = () => (
    <Tooltip
      content={ tooltipContent }
      disabled={ isMobile }
    >
      <Icon />
      { isMobile && (
        <span>{ mobileTitle }</span>
      ) }
    </Tooltip>
  );

  const pathContent = () => (
    <Tooltip
      placement={ tooltipPlacement }
      content={ tooltipContent }
      disabled={ isMobile }
    >
      <div onClick={ eventClick }>
        <Icon />
        { isMobile && (
          <span>{ mobileTitle }</span>
        ) }
      </div>
    </Tooltip>
  );

  const navigateToRoute = () => {
    route ? push(route) : window.location.href = path;
  };

  const checkTwoFactorModal = async () => {
    const session = await getSession({ clearCache: true });
    const visitedDatarooms = session![tenant]?.activeSessions || [];
    const isDataroomVisited = visitedDatarooms.indexOf(dataroom.id) !== -1;
    if (session!.two_factor || isDataroomVisited) {
      navigateToRoute();
      return;
    }
    setModalData({
      isModalOpen: true,
      entityName: dataroom.displayName,
      onSuccessCallback: navigateToRoute,
    });
  };

  if (variant === VariantType.Button) {
    return (
      <Tooltip
        content={ disabledButtonTooltip }
        disabled={ !disabled }
      >
        <Button
          type="button"
          variant={ isMobile ? ButtonVariantType.link : ButtonVariantType.action }
          className={ cn(className, styles.buttonAction) }
          onClick={ onClick }
          dataTest={ dataTest }
          disabled={ disabled }
        >
          { isMobile && (<IconCollapseSidebar />) }
          { buttonTitle }
        </Button>
      </Tooltip>
    );
  }

  if (route) {
    return (
      !isNeedToShowTwoFactorModal ?
        (
          <Link
            to={ route }
            onClick={ onClick }
            dataTest={ dataTest }
          >
            { routeContent() }
          </Link>
        ) : (
          <span
            className={ twoFactorSpanClassname }
            onClick={ checkTwoFactorModal }
            data-test={ dataTest }
          >
            { routeContent() }
          </span>
        )
    );
  }

  return (
    !isNeedToShowTwoFactorModal ? (
      <a
        href={ path }
        className={ cn(className, {
          [styles.disabled]: disabled,
          [styles.isActionItemVisible]: isActionItemVisible,
        }) }
        data-test={ dataTest }
      >
        { pathContent() }
      </a>
    ) : (
      <span
        onClick={ checkTwoFactorModal }
        className={ cn(twoFactorSpanClassname, className, {
          [styles.disabled]: disabled,
          [styles.isActionItemVisible]: isActionItemVisible,
        }) }
        data-test={ dataTest }
      >
        { pathContent() }
      </span>
    )
  );
};

export default ActionItem;
